import React, { useState, useContext, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Card,
  CardContent,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { ThemeContext } from "../../context/ThemeContext";
import { getColors } from "../../themes/theme";
import AddUserDialog from "../../components/users/AddUserDialog";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";
import { EditUserDialog } from "../../components/users/EditUserDialog";
import useMediaQuery from "@mui/material/useMediaQuery";

const UsersPage = () => {
  const isMobile = useMediaQuery("(max-width:1280px)");
  const { user, token } = useAuthContext();
  const isUserAdmin = user?.securityType === 2;

  const { darkMode } = useContext(ThemeContext);
  const [openDialog, setOpenDialog] = useState(false);
  const [userRole, setUserRole] = useState(""); // To track if user is admin or not
  const [users, setUsers] = useState([]); // State to store users fetched from backend
  const [page, setPage] = useState(0); // Pagination page
  const [rowsPerPage, setRowsPerPage] = useState(10); // Users per page
  const [loading, setLoading] = useState(true); // Loading state for fetching data
  const [error, setError] = useState(null); // Error state for failed requests
  const { enqueueSnackbar } = useSnackbar();

  const [openEditDialog, setOpenEditDialog] = useState(false); // New state for confirmation dialog
  const [userToEdit, setUserToEdit] = useState(null); // New state for the user to be deleted

  // Sorting state
  const [sortConfig, setSortConfig] = useState({
    key: "username", // Default sort key
    direction: "asc", // Default sort direction
  });

  useEffect(() => {
    // Fetch users from the backend
    if (!user || !token) return;
    const fetchUsers = async () => {
      try {
        let data;
        if (isUserAdmin) {
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/users/practiceid/${user?.practiceId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          data = response.data;
        }

        // Map the backend response to match your data structure
        const mappedUsers = data?.users.map((user) => ({
          userid: user.userid,
          username: user.username,
          email: user.email || "N/A",
          description: user.description || "N/A",
          status: user.suspended,
          role: user.securitytype,
        }));

        setUsers(mappedUsers);
      } catch (err) {
        setError("Failed to fetch users");
        console.error("Error fetching users:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [token, isUserAdmin, user]);

  const handleEdit = async (userId) => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/users/${userId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    setUserToEdit(response.data.user);
    setOpenEditDialog(true);
  };

  const handleCancelEdit = () => {
    setOpenEditDialog(false);
  };

  const handleDeleteUser = async (id) => {
    await axios.delete(`${process.env.REACT_APP_API_URL}/users/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    setUsers((prevUsers) => prevUsers.filter((user) => user.userid !== id));
    setOpenEditDialog(false);
    enqueueSnackbar("Successfully Deleted!", { variant: "success" });
  };

  const handleEditUser = async (data) => {
    await axios.put(
      `${process.env.REACT_APP_API_URL}/users/${userToEdit.userid}`,
      {
        username: data.username,
        password: data.password,
        email: data.email,
        description: data.description,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    setUsers((prevUsers) =>
      prevUsers.map((user) =>
        user.userid === userToEdit.userid
          ? {
              ...user,
              username: data.username,
              password: data.password,
              email: data.email,
              description: data.description,
            }
          : user
      )
    );
    setOpenEditDialog(false);
    enqueueSnackbar("Successfully Changed!", { variant: "success" });
  };

  const handleAddAdminClicked = () => {
    setUserRole("Admin");
    setOpenDialog(true);
  };

  const handleAddUserClicked = () => {
    setUserRole("User");
    setOpenDialog(true);
  };

  const handleAddUser = async (newUser) => {
    const newUserData = {
      ...newUser,
      suspended: 0,
      patientid: 0,
      practiceid: user.practiceId,
      securitytype: userRole === "Admin" ? 2 : 3,
    };

    // Send a POST request to the backend API to add the new user
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/users`,
      newUserData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    setUsers((prevUsers) => [
      ...prevUsers,
      {
        ...response.data.user,
        status: response.data.user.suspended,
        role: response.data.user.securitytype,
      },
    ]);
    enqueueSnackbar("Successfully Added!", { variant: "success" });
    setOpenDialog(false);
  };

  const handleSort = (column) => {
    const isAscending =
      sortConfig.key === column && sortConfig.direction === "asc";
    const newDirection = isAscending ? "desc" : "asc";

    setSortConfig({
      key: column,
      direction: newDirection,
    });

    // Sort users based on the column and direction
    const sortedUsers = [...users].sort((a, b) => {
      if (a[column] < b[column]) return newDirection === "asc" ? -1 : 1;
      if (a[column] > b[column]) return newDirection === "asc" ? 1 : -1;
      return 0;
    });

    setUsers(sortedUsers);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedUsers = users.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const roleColors = {
    1: "#FF0000", // Admin
    2: "#00AF4E", // Global User
    3: "#0A7CD9", // User
  };

  return (
    <Box
      px={{ xs: "10px", sm: 3, md: "30px", lg: 4, xl: 8 }}
      py={{ xs: 4, sm: 3, md: "30px", lg: 4, xl: 8 }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          // alignItems: "center",
          mb: 5,
        }}
      >
        <Typography
          sx={{
            fontFamily: "Poppins",
            fontSize: { xs: "24px", sm: "28px" },
            fontWeight: 500,
            color: darkMode
              ? getColors.drawerTextDark
              : getColors.thumbnailTextLight,
          }}
        >
          {isUserAdmin && "User Administration"}
        </Typography>
        <Box
          sx={{ display: "flex", alignSelf: "flex-end" }}
          gap={{ xs: 1, sm: 2, md: 3, lg: 4, xl: 5 }}
        >
          <Button
            startIcon={<Add />}
            variant="contained"
            sx={{
              mt: { xs: 2, sm: 0 },
              fontFamily: "Poppins",
              fontSize: { xs: "14px", sm: "18px" },
              px: { xs: "10px", sm: "16px" },
              py: { xs: "8px", sm: "12px" },
              background: darkMode ? "#2D2E30" : "#E6E6E6",
              color: darkMode
                ? getColors.primaryTextDark
                : getColors.secondaryTextLight,
              "&:hover": {
                color: getColors.primaryTextDark,
              },
            }}
            onClick={handleAddAdminClicked}
          >
            {isUserAdmin && "Add Administrator"}
          </Button>
          {isUserAdmin && (
            <Button
              startIcon={<Add />}
              variant="contained"
              sx={{
                mt: { xs: 2, sm: 0 },
                fontFamily: "Poppins",
                fontSize: { xs: "14px", sm: "18px" },
                px: { xs: "10px", sm: "16px" },
                py: { xs: "8px", sm: "12px" },
                background: darkMode ? "#2D2E30" : "#E6E6E6",
                color: darkMode
                  ? getColors.primaryTextDark
                  : getColors.secondaryTextLight,
                "&:hover": {
                  color: getColors.primaryTextDark,
                },
              }}
              onClick={handleAddUserClicked}
            >
              Add User
            </Button>
          )}
        </Box>
      </Box>

      {/* Loading State */}
      {loading && <Typography>Loading users...</Typography>}
      {error && <Typography color="error">{error}</Typography>}

      {!loading && !error && (
        <>
          {isMobile ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(1, 1fr)",
                  sm: "repeat(2, 1fr)",
                },
                gap: 3,
              }}
            >
              {paginatedUsers.map((user) => (
                <Card
                  key={user.userid}
                  sx={{
                    color: darkMode
                      ? getColors.primaryTextDark
                      : getColors.thumbnailTextLight,
                    borderRadius: "10px",
                    boxShadow: darkMode
                      ? "0px 4px 10px rgba(0,0,0, 0.9)"
                      : "0px 4px 10px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {user.username}
                    </Typography>
                    <Typography>Email: {user.email}</Typography>
                    <Typography>Description: {user.description}</Typography>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-end"
                    >
                      <Box>
                        <Box display="flex">
                          <Typography>Status:</Typography>
                          <Typography
                            sx={{
                              ml: 1,
                              color: user.status === 0 ? "#00AF4E" : "#FF0000",
                            }}
                          >
                            {user.status === 0 ? "Active" : "Suspended"}
                          </Typography>
                        </Box>
                        <Box display="flex">
                          <Typography>Role:</Typography>
                          <Typography
                            sx={{
                              color: roleColors[user.role],
                              ml: 1,
                            }}
                          >
                            {user.role === 2
                              ? "User Admin"
                              : user.role === 3
                              ? "User"
                              : "Unknown"}
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        onClick={() => handleEdit(user.userid)}
                        sx={{
                          textAlign: "center",
                          fontSize: "18px",
                          color: "#007FFF",
                          pl: 6,
                          borderColor: "#007FFF",
                        }}
                      >
                        Edit
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} sx={{ mb: 1 }}>
                <Table>
                  <TableHead
                    sx={{
                      background: darkMode ? "#1C1D21" : "#E6E6E6",
                      borderWidth: 1,
                      borderStyle: "solid",
                      borderColor: darkMode
                        ? getColors.appBarBorderDark
                        : getColors.thumbnailBorderLight,
                    }}
                  >
                    <TableRow sx={{ height: "60px" }}>
                      {[
                        "username",
                        "email",
                        "description",
                        "status",
                        "role",
                      ].map((header, index) => (
                        <TableCell
                          key={index}
                          sx={{
                            borderColor: darkMode
                              ? getColors.appBarBorderDark
                              : getColors.thumbnailBorderLight,
                            px: 0,
                            cursor: "pointer",
                          }}
                          onClick={() => handleSort(header)}
                        >
                          <Box
                            display="flex"
                            sx={{
                              width: "100%",
                              height: "100%",
                              alignItems: "center",
                              fontFamily: "Poppins",
                              fontWeight: 500,
                              fontSize: {
                                xs: "12px",
                                sm: "14px",
                                md: "16px",
                                lg: "18px",
                                xl: "20px",
                              },
                              color: darkMode
                                ? getColors.listItemTextDark
                                : getColors.thumbnailTextLight,
                              ...(header === "username" ||
                              header === "email" ||
                              header === "description"
                                ? {
                                    px: {
                                      xs: "12px",
                                      sm: "14px",
                                      md: "16px",
                                      lg: "18px",
                                      xl: "20px",
                                    },
                                  }
                                : { justifyContent: "center" }),
                            }}
                          >
                            {header.toUpperCase()}
                            {sortConfig.key === header && (
                              <>
                                {sortConfig.direction === "asc" ? (
                                  <ArrowDownwardIcon sx={{ ml: 1 }} />
                                ) : (
                                  <ArrowUpwardIcon sx={{ ml: 1 }} />
                                )}
                              </>
                            )}
                          </Box>
                        </TableCell>
                      ))}
                      <TableCell
                        sx={{
                          fontFamily: "Poppins",
                          fontWeight: 500,
                          fontSize: {
                            xs: "12px",
                            sm: "14px",
                            md: "16px",
                            lg: "18px",
                            xl: "20px",
                          },
                          color: darkMode
                            ? getColors.listItemTextDark
                            : getColors.thumbnailTextLight,
                          textAlign: "center",
                          borderColor: darkMode
                            ? getColors.appBarBorderDark
                            : getColors.thumbnailBorderLight,
                        }}
                      >
                        ACTIONS
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {paginatedUsers.map((user) => (
                      <TableRow
                        key={user.userid}
                        sx={{
                          height: "60px",
                          borderWidth: 1,
                          borderStyle: "solid",
                          borderColor: darkMode
                            ? getColors.appBarBorderDark
                            : getColors.thumbnailBorderLight,
                        }}
                      >
                        <TableCell
                          sx={{
                            fontFamily: "Inter",
                            fontWeight: 500,
                            fontSize: {
                              xs: "12px",
                              sm: "14px",
                              md: "16px",
                              lg: "18px",
                              xl: "20px",
                            },
                            color: darkMode
                              ? getColors.listItemTextDark
                              : getColors.thumbnailTextLight,
                            px: {
                              xs: "12px",
                              sm: "14px",
                              md: "16px",
                              lg: "18px",
                              xl: "20px",
                            },
                            borderWidth: 1,
                            borderStyle: "solid",
                            borderColor: darkMode
                              ? getColors.appBarBorderDark
                              : getColors.thumbnailBorderLight,
                          }}
                        >
                          {user.username}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "Inter",
                            fontWeight: 400,
                            fontSize: {
                              xs: "12px",
                              sm: "14px",
                              md: "16px",
                              lg: "18px",
                              xl: "20px",
                            },
                            color: darkMode
                              ? getColors.listItemTextDark
                              : getColors.thumbnailTextLight,
                            px: {
                              xs: "12px",
                              sm: "14px",
                              md: "16px",
                              lg: "18px",
                              xl: "20px",
                            },
                            borderWidth: 1,
                            borderStyle: "solid",
                            borderColor: darkMode
                              ? getColors.appBarBorderDark
                              : getColors.thumbnailBorderLight,
                          }}
                        >
                          {user.email}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "Inter",
                            fontWeight: 400,
                            fontSize: {
                              xs: "12px",
                              sm: "14px",
                              md: "16px",
                              lg: "18px",
                              xl: "20px",
                            },
                            color: darkMode
                              ? getColors.listItemTextDark
                              : getColors.thumbnailTextLight,
                            px: {
                              xs: "12px",
                              sm: "14px",
                              md: "16px",
                              lg: "18px",
                              xl: "20px",
                            },
                            borderWidth: 1,
                            borderStyle: "solid",
                            borderColor: darkMode
                              ? getColors.appBarBorderDark
                              : getColors.thumbnailBorderLight,
                          }}
                        >
                          {user.description}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "Inter",
                            fontWeight: 400,
                            fontSize: {
                              xs: "12px",
                              sm: "14px",
                              md: "16px",
                              lg: "18px",
                              xl: "20px",
                            },
                            color: user.status === 0 ? "#00AF4E" : "#FF0000",
                            px: {
                              xs: "12px",
                              sm: "14px",
                              md: "16px",
                              lg: "18px",
                              xl: "20px",
                            },
                            borderWidth: 1,
                            borderStyle: "solid",
                            borderColor: darkMode
                              ? getColors.appBarBorderDark
                              : getColors.thumbnailBorderLight,
                            textAlign: "center",
                          }}
                        >
                          {user.status === 0 ? "Active" : "Suspended"}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "Inter",
                            fontWeight: 400,
                            fontSize: {
                              xs: "12px",
                              sm: "14px",
                              md: "16px",
                              lg: "18px",
                              xl: "20px",
                            },
                            color: roleColors[user.role],
                            px: {
                              xs: "12px",
                              sm: "14px",
                              md: "16px",
                              lg: "18px",
                              xl: "20px",
                            },
                            borderWidth: 1,
                            borderStyle: "solid",
                            borderColor: darkMode
                              ? getColors.appBarBorderDark
                              : getColors.thumbnailBorderLight,
                            textAlign: "center",
                          }}
                        >
                          {user.role === 2 && "User Admin"}
                          {user.role === 3 && "User"}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "center",
                            borderColor: darkMode
                              ? getColors.appBarBorderDark
                              : getColors.thumbnailBorderLight,
                          }}
                        >
                          <Box
                            display={"flex"}
                            justifyContent={"center"}
                            alignItems={"center"}
                            sx={{ cursor: "pointer" }}
                            onClick={() => handleEdit(user.userid)}
                          >
                            <Box
                              display={"flex"}
                              justifyContent={"flex-end"}
                              width="max-content"
                              sx={{
                                borderRadius: "50%",
                                padding: "8px",
                                color: "#007FFF",
                                bgcolor: "#E9F3FF",
                                alignItems: "center",
                                "&:hover": {
                                  color: "blue",
                                },
                              }}
                            >
                              <BorderColorOutlinedIcon />
                            </Box>
                            <Typography
                              sx={{
                                ml: "13px",
                                fontFamily: "Inter",
                                fontWeight: 500,
                                fontSize: {
                                  xs: "10px",
                                  sm: "12px",
                                  md: "14px",
                                  lg: "16px",
                                  xl: "18px",
                                },
                                color: darkMode
                                  ? getColors.listItemTextDark
                                  : getColors.thumbnailTextLight,
                              }}
                            >
                              Edit
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <TablePagination
                rowsPerPageOptions={[10, 20, 50, 100]}
                component="div"
                count={users.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}

          <EditUserDialog
            open={openEditDialog}
            onClose={handleCancelEdit}
            userData={userToEdit}
            onDelete={handleDeleteUser}
            onEdit={handleEditUser}
          ></EditUserDialog>

          {/* Dialog for adding User/Admin */}
          <AddUserDialog
            open={openDialog}
            setOpen={setOpenDialog}
            role={userRole}
            onAddUser={handleAddUser}
          />
        </>
      )}
    </Box>
  );
};

export default UsersPage;
